import * as THREE from 'three';
import type { BlockPhysics } from '../world/collision';
import type { LootDrop, LootKind } from './droptable';
import { VERTICAL_ENGAGEMENT_RANGE } from '../combat/spatial';
import { RARITY_COLOR, type Rarity } from '../economy/rarity';
import { WEARABLES } from '../economy/wardrobe';

const MAX_PICKUPS = 64; // pool cap — §3's "pool everything" budget discipline
const GRAVITY = -14;
const POP_SPEED = 2.4; // initial upward hop, so a drop visibly "pops" out of the kill rather than just appearing
const SPAWN_XZ_JITTER = 0.5; // meters — keeps several same-tick drops from stacking exactly on top of each other
const FALL_STEP_TOLERANCE = 0.25; // matches PlayerCapsule's own STEP_TOLERANCE idiom for "fall until you land on whatever's below" — small on purpose so a pickup dropped mid-air (a flyer's loot) genuinely falls PAST any floor it hasn't reached yet instead of snapping down early
const MAGNET_RADIUS = 3.2;
const MAGNET_SPEED = 9;
const COLLECT_RADIUS = 0.55;
const LIFETIME = 25; // seconds an uncollected pickup survives before despawning, so an ignored corner doesn't slowly exhaust the pool
const EXPIRY_BLINK_WINDOW = 5; // I-1 (PLAN-CITY3 §5): "5s blink-out warning, no burst" — the last 5s of LIFETIME visibly blinks instead of just vanishing
const EXPIRY_BLINK_HZ = 6; // full on/off cycles per second during the blink window
const SPIN_SPEED = 2.2; // rad/s — I-2's "spin+bob" for every settled pickup
const BOB_AMPLITUDE = 0.06; // meters — visual-only offset, never touches p.position (the real collect/magnet radius reference)
const BOB_FREQ = 3.4; // rad/s
// Review fix (HIGH, correctness): a small step-down/ramp/curb should still
// fall and settle normally — only a genuinely large gap (a rooftop kill
// whose nearest real floor is the street, ~16m down) counts as
// "unreachable" and pins at kill-level instead. See spawn()'s own comment
// for why a plain `groundHeightAt === null` check alone doesn't catch this.
const UNREACHABLE_FALL_GUARD_MAX = 4;
// Imported, not duplicated (S04 review finding — a hand-copied literal here
// would silently drift from combat/spatial.ts's own value the next time
// someone tunes engagement range for a new multi-floor habitat): a settled
// pickup on a floor level the player isn't actually on (rooftop loot while
// standing on the street below it) must not magnet-pull or collect through
// the floor, using the SAME vertical window combat already engages across.
const VERTICAL_MAGNET_RANGE = VERTICAL_ENGAGEMENT_RANGE;

const COLORS: Record<LootKind, number> = {
  coin: 0xffd23f,
  material: 0x7fd1ff,
  heart: 0xff5c7a,
  // Wave L-1: overridden per-spawn by the rolled rarity's own color
  // (RARITY_COLOR) — these are just the pre-rarity-lookup fallback, never
  // actually seen in practice since every 'part'/'cosmetic' spawn always
  // carries a rarity (see spawn()'s own rarity parameter).
  part: RARITY_COLOR.uncommon,
  cosmetic: RARITY_COLOR.epic,
  // Wave I (I-4): a placed collectible has no rarity roll — a fixed, calm
  // teal-white distinct from every other kind's own fixed/rarity color.
  collectible: 0xd6f5ff,
};

/**
 * I-2 (PLAN-CITY3 §5): "separated colors (legendary != coin gold, rare !=
 * material blue)" — `RARITY_COLOR.legendary` (0xffc94a) sits only ~10 RGB
 * units from coin gold (0xffd23f), and `.rare` (0x59a7ff) sits only ~40 RGB
 * units from material blue (0x7fd1ff); at pickup scale (a ~0.14-0.2m mesh)
 * that's genuinely hard to tell apart at a glance. This overrides ONLY the
 * pickup mesh's own body color for the two colliding tiers — the BEAM
 * (below) and every other rarity-tinted surface in the game (wardrobe
 * cards, toasts) keep the canonical `RARITY_COLOR` unchanged, since those
 * contexts never sit next to a plain coin/material pickup on screen the way
 * a dropped part does.
 */
const PICKUP_RARITY_TINT: Partial<Record<Rarity, number>> = {
  rare: 0x2f6fe0, // a deeper, more saturated blue than material's pale 0x7fd1ff
  legendary: 0xff7a1a, // amber-orange, distinct from coin's pure gold 0xffd23f
};

/**
 * Exported (review fix, MEDIUM, spec-fidelity): `combat/kill.ts`'s
 * guaranteed-cosmetic celebration burst (main.ts's `grantWearableDirect`)
 * used the raw, non-tinted `RARITY_COLOR` because this table used to be
 * module-private — reproducing the exact legendary/coin-gold collision this
 * table exists to fix, for precisely the Rat King's crown and the Golden
 * Pigeon's plume (this game's two guaranteed-first-kill grants, both
 * `rarity:'legendary'`). Every consumer of a pickup-mesh-scale rarity tint
 * — inside this file or out — should call this, not index either table directly.
 */
export function pickupTintFor(rarity: Rarity): number {
  return PICKUP_RARITY_TINT[rarity] ?? RARITY_COLOR[rarity];
}

/** I-2: per-kind pooled geometry ("spinning coin disc, boxes for materials, beamed spheres for cosmetics") — swapped onto the shared per-slot Mesh at spawn time rather than each kind owning its own sub-pool, so MAX_PICKUPS stays one shared budget regardless of the kind mix in play. */
function geometryForKind(kind: LootKind, discGeo: THREE.CylinderGeometry, boxGeo: THREE.BoxGeometry, sphereGeo: THREE.SphereGeometry): THREE.BufferGeometry {
  if (kind === 'coin') return discGeo;
  if (kind === 'material' || kind === 'part') return boxGeo;
  return sphereGeo; // heart, cosmetic, collectible
}

// L-4 "rare-drop juice": a thin, tall, additive-blended beam standing over
// an UNCOMMON+ pickup — height/opacity scale with rarity so a LEGENDARY drop
// reads as visibly more of a moment than an UNCOMMON one, per Karan's own
// "the moment must feel like the moment" line (PLAN-CITY2 §6 L-4).
const BEAM_HEIGHT: Record<Rarity, number> = { common: 0, uncommon: 1.6, rare: 2.2, epic: 3, legendary: 4 };
const BEAM_OPACITY: Record<Rarity, number> = { common: 0, uncommon: 0.35, rare: 0.45, epic: 0.55, legendary: 0.7 };
const BEAM_RADIUS = 0.1;

export interface Pickup {
  active: boolean;
  kind: LootKind;
  mesh: THREE.Mesh;
  beam: THREE.Mesh;
  /** Set only while `kind === 'cosmetic'` — which wearable this pickup grants on collection (see `LootDrop.wearableId`). */
  wearableId: LootDrop['wearableId'];
  /** Wave I (I-4) — set only while `kind === 'collectible'`. See `LootDrop.persistentId`. */
  persistentId: string | null;
  position: THREE.Vector3;
  velocityY: number;
  settled: boolean;
  life: number;
  /** Review fix (HIGH, correctness): the spin+bob visual used to drive its sine phase off `life`, which is `Infinity` for a persistent collectible (`spawnPersistentCollectible`'s own `lifetimeOverride`) — `Math.sin(Infinity)` is NaN, silently corrupting `mesh.position.y` every frame once settled (the pickup stays fully collectible, since `position` itself is untouched, but renders invisible). A dedicated, always-finite phase accumulator (incremented by real `dt` every tick, independent of `life`) fixes this and is honestly the more correct design regardless — bob phase and time-to-expiry were never actually the same concept. */
  bobPhase: number;
}

/** `PickupPool.update()`'s own return shape — Wave I extends `LootDrop` with the two extra fields the COLLECTION moment needs but the economy/wallet grant itself (`loot/collect.ts`) doesn't: where it happened (the fly-arc/burst VFX) and, for a persistent collectible, which one. A superset of `LootDrop`, so it flows into `applyCollectedLoot(drops: LootDrop[], ...)` unchanged. */
export interface CollectedDrop extends LootDrop {
  position: THREE.Vector3;
}

/**
 * Pooled loot pickups (coin/material/heart), spawned on a kill (§0.7's
 * single choke point, `combat/kill.ts`). Falls under real gravity against
 * `BlockPhysics` — deliberately the SAME small-tolerance per-tick fall
 * PlayerCapsule already uses for ordinary gravity (not `teleport`'s huge-
 * tolerance snap), so a flyer's loot genuinely falls to the real floor
 * beneath it instead of settling at flight height or punching through to a
 * floor two levels down. Once settled, a pickup within `MAGNET_RADIUS` of
 * the player eases toward them and is collected at `COLLECT_RADIUS` —
 * `update()` returns the kinds collected this tick; crediting them (coins/
 * materials to the wallet, hearts as a heal) is the CALLER's job, same
 * separation `combat/resolve.ts` already keeps between "the pool advances
 * physics" and "damage/economy effects get applied."
 */
export interface SpawnRarityOpts {
  /** `kind:'cosmetic'` only — which wearable this pickup grants; its rarity is read from `WEARABLES` (never passed separately, so the two can't drift). */
  wearableId?: LootDrop['wearableId'];
  /** `kind:'part'` only — `economy/drops.ts`'s own `RolledExtras.partsRarity`. Ignored for every other kind (their rarity is either fixed — cosmetic — or n/a — coin/material/heart). */
  rarity?: Rarity;
  /** `kind:'collectible'` only — see `LootDrop.persistentId`. Prefer `spawnPersistentCollectible` over passing this directly. */
  persistentId?: string;
  /** Overrides `LIFETIME` — `Infinity` is how `spawnPersistentCollectible` makes a pickup never expire (the existing `life -= dt; if (life<=0)` check in `update()` never trips for it, no separate branch needed). */
  lifetimeOverride?: number;
  /** Skips `SPAWN_XZ_JITTER` — for a curated, hand-placed spawn (`spawnPersistentCollectible`) where landing at the EXACT given position matters, unlike a kill's own multi-drop stacking-avoidance jitter. */
  noJitter?: boolean;
}

const BURST_COUNT = 8; // I-1: "pooled ring quads" for the collection-only burst — small and cheap, several can overlap during a multi-kill moment without ever needing more than this
const BURST_DURATION = 0.35;

interface BurstRing {
  mesh: THREE.Mesh;
  active: boolean;
  t: number;
}

export class PickupPool {
  readonly group = new THREE.Group();
  private readonly pool: Pickup[] = [];
  // I-2: per-kind geometry — a shared disc/box/sphere the shared per-slot
  // Mesh's own `.geometry` reference swaps onto at spawn time (see
  // `geometryForKind`), not a separate sub-pool per shape.
  private readonly discGeo = new THREE.CylinderGeometry(0.15, 0.15, 0.045, 14);
  private readonly boxGeo = new THREE.BoxGeometry(0.19, 0.19, 0.19);
  private readonly geo = new THREE.SphereGeometry(0.14, 8, 6); // heart / cosmetic / collectible
  private readonly beamGeo = new THREE.CylinderGeometry(BEAM_RADIUS, BEAM_RADIUS * 0.4, 1, 8, 1, true);
  private readonly burstGeo = new THREE.RingGeometry(0.06, 0.24, 16);
  private readonly burstPool: BurstRing[] = [];
  /** I-2's unreachable-drop guard — set once via `setPhysics` (a small, additive post-construction wire-up, the same pattern `OrbitCamera.setOccluders` already uses in this codebase). `null` until then, so a pickup spawned before main.ts finishes wiring simply falls with the old (guardless) behavior rather than throwing. */
  private physics: BlockPhysics | null = null;

  constructor() {
    for (let i = 0; i < MAX_PICKUPS; i++) {
      const mat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.35, metalness: 0.25 });
      const mesh = new THREE.Mesh(this.geo, mat);
      mesh.visible = false;
      const beamMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
      const beam = new THREE.Mesh(this.beamGeo, beamMat);
      beam.visible = false;
      this.group.add(mesh, beam);
      this.pool.push({ active: false, kind: 'coin', mesh, beam, wearableId: undefined, persistentId: null, position: new THREE.Vector3(), velocityY: 0, settled: false, life: 0, bobPhase: 0 });
    }
    for (let i = 0; i < BURST_COUNT; i++) {
      const mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
      const mesh = new THREE.Mesh(this.burstGeo, mat);
      mesh.rotation.x = -Math.PI / 2; // lies flat, a ground-level pulse ring rather than a billboard
      mesh.visible = false;
      this.group.add(mesh);
      this.burstPool.push({ mesh, active: false, t: 0 });
    }
  }

  /** Wired once from main.ts right after both `pickups` and `block` exist — see `physics`'s own field comment. */
  setPhysics(physics: BlockPhysics): void {
    this.physics = physics;
  }

  private oldestActiveOfKind(kind: LootKind): Pickup | undefined {
    let best: Pickup | undefined;
    for (const p of this.pool) {
      if (p.active && p.kind === kind && (!best || p.life < best.life)) best = p;
    }
    return best;
  }

  spawn(kind: LootKind, origin: THREE.Vector3, opts?: SpawnRarityOpts): void {
    let p = this.pool.find((slot) => !slot.active);
    if (!p) {
      // L6 (PLAN-CITY3 §5, I-4): a cosmetic is always epic/legendary-tier —
      // it must never be the drop that silently loses out to a full pool of
      // ordinary coins (CITY3-FINDINGS.md's own boss-cosmetic-spawns-last
      // bug, closed at the ORDER level in combat/kill.ts — this is the
      // pool's own last line of defense for the rare case a fight is
      // already saturated regardless of order). Evict the oldest (soonest-
      // to-naturally-expire) active coin first, falling back to material —
      // never a part/heart/collectible, which can carry their own
      // meaningful rarity/permanence.
      if (kind === 'cosmetic') p = this.oldestActiveOfKind('coin') ?? this.oldestActiveOfKind('material');
      if (!p) return; // pool exhausted and nothing evictable — drop silently, same discipline as ProjectilePool.fire
    }
    p.active = true;
    p.kind = kind;
    p.wearableId = kind === 'cosmetic' ? opts?.wearableId : undefined;
    p.persistentId = kind === 'collectible' ? (opts?.persistentId ?? null) : null;
    p.settled = false;
    p.life = opts?.lifetimeOverride ?? LIFETIME;
    p.bobPhase = 0;
    p.velocityY = POP_SPEED * (0.7 + Math.random() * 0.6);
    // Review fix (LOW, correctness): a curated placement (spawnPersistentCollectible)
    // must land EXACTLY at its own world position — the ±0.25m/axis jitter
    // below exists only to keep several same-tick KILL drops from stacking
    // on top of each other (see SPAWN_XZ_JITTER's own comment), a rationale
    // that doesn't apply to a one-time, hand-placed collectible and risks
    // carrying it off a narrow ledge's own footprint.
    const jitter = opts?.noJitter ? 0 : SPAWN_XZ_JITTER;
    p.position.set(origin.x + (Math.random() - 0.5) * jitter, origin.y + 0.3, origin.z + (Math.random() - 0.5) * jitter);
    p.mesh.visible = true;
    p.mesh.geometry = geometryForKind(kind, this.discGeo, this.boxGeo, this.geo);
    p.mesh.position.copy(p.position);
    p.mesh.rotation.set(0, 0, 0);
    p.mesh.scale.setScalar(1);

    // Review fix (HIGH, spec-fidelity): the ORIGINAL guard only checked for
    // a `null` groundHeightAt result — but `world/layout.ts`'s STREET_FLOOR
    // is a flat, priority-0 floor spanning the ENTIRE playable square, so
    // groundHeightAt (no lower bound on its tolerance window) essentially
    // NEVER returns null for any in-bounds XZ, even directly below a real
    // roof edge — the exact "roof-edge kill" case this guard exists for.
    // Real fix: compare the ACTUAL rest height groundHeightAt found against
    // the spawn height — a normal small step-down (a curb, a ramp, a low
    // ledge) stays well under UNREACHABLE_FALL_GUARD_MAX; a kill atop an
    // elevated surface with nothing genuinely climbable below (a rooftop,
    // whose only "floor" in that XZ column is the street far below) exceeds
    // it and settles at kill-level instead of free-falling the full height.
    // `ground === null` (truly no floor registered anywhere, e.g. off the
    // map entirely) still settles immediately too, same as before.
    const groundBelow = this.physics ? this.physics.groundHeightAt(p.position.x, p.position.z, p.position.y, FALL_STEP_TOLERANCE) : null;
    if (this.physics && (groundBelow === null || p.position.y - groundBelow > UNREACHABLE_FALL_GUARD_MAX)) {
      p.settled = true;
      p.velocityY = 0;
    }

    // L-1/L-4: a cosmetic's rarity comes from its own WEARABLES entry (never
    // passed loose — can't drift from the registry); a part's rarity is
    // whatever economy/drops.ts rolled. Every other kind has no beam.
    const rarity: Rarity | null = kind === 'cosmetic' && p.wearableId ? WEARABLES[p.wearableId].rarity : kind === 'part' ? (opts?.rarity ?? null) : null;
    // I-2: the pickup MESH itself uses the tint override where one exists
    // (see PICKUP_RARITY_TINT's own comment) — the beam below stays on the
    // canonical RARITY_COLOR, matching every other rarity-tinted surface.
    const meshColor = rarity ? pickupTintFor(rarity) : COLORS[kind];
    (p.mesh.material as THREE.MeshStandardMaterial).color.setHex(meshColor);

    const beamMat = p.beam.material as THREE.MeshBasicMaterial;
    if (rarity && BEAM_HEIGHT[rarity] > 0) {
      p.beam.visible = true;
      p.beam.scale.set(1, BEAM_HEIGHT[rarity], 1);
      p.beam.position.copy(p.position);
      p.beam.position.y += BEAM_HEIGHT[rarity] / 2;
      beamMat.color.setHex(RARITY_COLOR[rarity]);
      beamMat.opacity = BEAM_OPACITY[rarity];
    } else {
      p.beam.visible = false;
    }
  }

  /** The reusable "persistent collectible" primitive (PLAN-CITY3 §5, I-4) future waves (E-3/V-3/N-1/N-2/N-4) place ONE of per new destination — never expires, and is a genuine no-op if the save already recorded `id` found, so a placing wave doesn't need to re-derive that check itself. */
  spawnPersistentCollectible(id: string, origin: THREE.Vector3, alreadyFound: boolean): void {
    if (alreadyFound) return;
    this.spawn('collectible', origin, { persistentId: id, lifetimeOverride: Infinity, noJitter: true });
  }

  /** I-1: "collect burst (pooled ring quads, rarity-tinted, collection ONLY)" — also reused directly by main.ts for a guaranteed-cosmetic's own kill-moment celebration (combat/kill.ts's `grantWearableDirect`), so both paths share one visual language for "you got something." */
  spawnBurst(position: THREE.Vector3, color: number): void {
    const b = this.burstPool.find((x) => !x.active) ?? this.burstPool[0]; // reuse the oldest slot under heavy simultaneous pickups — a missed burst is a cosmetic-only compromise, never a functional loss
    b.active = true;
    b.t = 0;
    b.mesh.visible = true;
    b.mesh.position.set(position.x, position.y + 0.05, position.z);
    b.mesh.scale.setScalar(1);
    const mat = b.mesh.material as THREE.MeshBasicMaterial;
    mat.color.setHex(color);
    mat.opacity = 0.9;
  }

  private updateBursts(dt: number): void {
    for (const b of this.burstPool) {
      if (!b.active) continue;
      b.t += dt;
      const k = Math.min(1, b.t / BURST_DURATION);
      b.mesh.scale.setScalar(1 + k * 2.2);
      (b.mesh.material as THREE.MeshBasicMaterial).opacity = 0.9 * (1 - k);
      if (k >= 1) {
        b.active = false;
        b.mesh.visible = false;
      }
    }
  }

  /** Advances fall/settle/magnet physics, collection, and the burst/spin/bob/blink visuals. Returns the drops collected THIS tick (kind + wearableId/persistentId + world position — `economy`/`main.ts` credit/react to them, same separation as before). */
  update(dt: number, physics: BlockPhysics, playerPos: THREE.Vector3, playerRadius: number): CollectedDrop[] {
    const collected: CollectedDrop[] = [];
    this.updateBursts(dt);
    for (const p of this.pool) {
      if (!p.active) continue;

      p.life -= dt; // Infinity - dt === Infinity — a persistent collectible's own lifetimeOverride never trips this
      if (p.life <= 0) {
        this.deactivate(p);
        continue;
      }

      if (!p.settled) {
        p.velocityY += GRAVITY * dt;
        const newY = p.position.y + p.velocityY * dt;
        const ground = physics.groundHeightAt(p.position.x, p.position.z, p.position.y, FALL_STEP_TOLERANCE);
        if (ground !== null && newY <= ground) {
          p.position.y = ground;
          p.velocityY = 0;
          p.settled = true;
        } else {
          p.position.y = newY;
        }
      } else {
        const dx = playerPos.x - p.position.x;
        const dz = playerPos.z - p.position.z;
        const dy = Math.abs(playerPos.y - p.position.y);
        const horizDist = Math.hypot(dx, dz);
        // Vertical gate mirrors combat/spatial.ts's engagement window — a
        // settled pickup on a floor level the player isn't actually on
        // (rooftop loot while standing on the street below it) must not
        // magnet-pull or collect through the floor between them.
        if (dy <= VERTICAL_MAGNET_RANGE && horizDist <= MAGNET_RADIUS && horizDist > 1e-4) {
          const pull = Math.min(1, (MAGNET_SPEED * dt) / horizDist);
          p.position.x += dx * pull;
          p.position.z += dz * pull;
        }
        if (dy <= VERTICAL_MAGNET_RANGE && horizDist <= COLLECT_RADIUS + playerRadius) {
          // I-1: the collect burst fires HERE, collection only — never on
          // natural expiry (that's the blink below instead, a deliberately
          // DIFFERENT visual so the two outcomes never look pixel-identical).
          this.spawnBurst(p.position, (p.mesh.material as THREE.MeshStandardMaterial).color.getHex());
          collected.push({ kind: p.kind, wearableId: p.wearableId, persistentId: p.persistentId ?? undefined, position: p.position.clone() });
          this.deactivate(p);
          continue;
        }
      }

      // I-2: spin+bob for every settled pickup — visual only, applied to the
      // MESH's own transform, never to `p.position` itself (the true
      // physics/collect/magnet reference every branch above reads).
      // Review fix (HIGH, correctness): phase comes from `bobPhase` (always
      // finite, driven by real `dt`), NOT `life` — a persistent collectible's
      // `life` is `Infinity` (spawnPersistentCollectible's own
      // lifetimeOverride), and `Math.sin(Infinity)` is NaN, which used to
      // silently corrupt `mesh.position.y` every frame once settled (fully
      // still collectible — `position` is untouched — just invisible).
      p.mesh.position.copy(p.position);
      if (p.settled) {
        p.bobPhase += dt;
        p.mesh.rotation.y += SPIN_SPEED * dt;
        p.mesh.position.y += BOB_AMPLITUDE * Math.sin(p.bobPhase * BOB_FREQ);
      }
      // I-1: "expiry becomes visually distinct (5s blink-out warning, no
      // burst)" — a settled, still-uncollected pickup blinks for the last
      // EXPIRY_BLINK_WINDOW seconds of its life instead of just vanishing at
      // 0, so the player gets an actual warning it's about to despawn.
      // Falling/magnet-pulling pickups are excluded — a fast-falling drop
      // flickering mid-air would read as a rendering glitch, not a warning.
      if (p.settled && p.life <= EXPIRY_BLINK_WINDOW) {
        p.mesh.visible = Math.floor(p.life * EXPIRY_BLINK_HZ) % 2 === 0;
      } else {
        p.mesh.visible = true;
      }
      if (p.beam.visible) {
        p.beam.position.x = p.position.x;
        p.beam.position.z = p.position.z;
        p.beam.position.y = p.position.y + p.beam.scale.y / 2;
      }
    }
    return collected;
  }

  private deactivate(p: Pickup): void {
    p.active = false;
    p.mesh.visible = false;
    p.beam.visible = false;
  }

  /** Same clean-slate reset ProjectilePool exposes — dev/harness use only. */
  clearAll(): void {
    for (const p of this.pool) if (p.active) this.deactivate(p);
  }

  activeCount(): number {
    let n = 0;
    for (const p of this.pool) if (p.active) n++;
    return n;
  }

  /** Same dev/harness inspection convention as `combat/projectiles.ts`'s `ProjectilePool.getActive()`. */
  getActive(): readonly Pickup[] {
    return this.pool.filter((p) => p.active);
  }
}
